import { descend, flatten, map, prop, sort } from "ramda";
import { type CSSProperties, type FC, type ReactElement, useCallback, useEffect, useState } from "react";
import type { FormatOptionLabelMeta } from "react-select";
import WindowedSelect from "react-windowed-select";
import { Tag, TimeAgo } from "widgets";
import type { GetArrayElementType } from "widgets/utils";

import { type ISelectOption } from "@/app-utils/form.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { type Group, type ProjectFile, type SavedItem, type View } from "@/graphql.ts";
import {
  type GetAllFilesInProjectForAttachmentsQuery,
  useGetAllFilesInProjectForAttachmentsLazyQuery,
} from "@/modules/projects/graphql/projects-queries.gql.js";
import {
  type GetSavedItemsForAttachmentsQuery,
  useGetSavedItemsForAttachmentsLazyQuery,
  useGetSavedItemsListingLazyQuery,
} from "@/modules/saved-items/graphql/saved-items.gql.js";
import { getViewTitle } from "@/redux/modules/saved-view.ts";
import { ENTITY_TYPE, FILE_TYPE, TAG_COLOR } from "@/settings/settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

const ATTACHMENT_PLACEHOLDER_TEXT = "Search...";

export interface ISelectAttachmentOption extends ISelectOption {
  id: number;
  type: string;
  created: string;
  owner: string;
  //count?: number;
  //viewType?: string;
  description?: string;
  organisms?: string[];
  parents?: string[];
  sampleTypes?: string[];
}

interface IAttachmentSelectPanelProps {
  selectedAttachments: ISelectAttachmentOption[];
  projectId: number;
  onSelect: (options: ISelectAttachmentOption[]) => void;
}

type SavedItemsAttachment = GetArrayElementType<GetSavedItemsForAttachmentsQuery["savedItems"]["items"]>;
type ProjectFileAttachment = GetArrayElementType<GetAllFilesInProjectForAttachmentsQuery["projectFiles"]>;

export const AttachmentSelectPanel: FC<IAttachmentSelectPanelProps> = (props) => {
  const [isLoading, setLoading] = useState(false);
  const [savedItemsForAttachment, setSavedItemsForAttachment] = useState<SavedItemsAttachment[] | null>(null);
  const [itemOptions, setItemOptions] = useState<ISelectAttachmentOption[]>([]);
  const [projectFilesForAttachment, setProjectFilesForAttachment] = useState<ProjectFileAttachment[] | null>(null);

  const [getSavedItemsListing] = useGetSavedItemsListingLazyQuery();
  const [getSavedItemsForAttachments] = useGetSavedItemsForAttachmentsLazyQuery();
  const [getAllFilesInProjectForAttachments] = useGetAllFilesInProjectForAttachmentsLazyQuery();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setSavedItemsForAttachment(null);
      setProjectFilesForAttachment(null);

      // attachments
      const { data } = await getSavedItemsListing({ variables: { projectId: props.projectId, tagIds: null } });
      const listing = data.savedItems.listing;

      const response = await Promise.all(
        map(
          ({ type, subType }) =>
            getSavedItemsForAttachments({ variables: { projectId: props.projectId, type, subType } }),
          listing,
        ),
      );
      const attachments = flatten(map((resp) => resp.data.savedItems.items, response));

      // files
      const { data: filesData } = await getAllFilesInProjectForAttachments({
        variables: { projectId: props.projectId },
      });
      const files = filesData.projectFiles;

      setSavedItemsForAttachment(attachments);
      setProjectFilesForAttachment(files);
      setLoading(false);
    };

    void load();
  }, [props.projectId]);

  useEffect(() => {
    let optionList: ISelectAttachmentOption[] = [];

    if (savedItemsForAttachment) {
      optionList = savedItemsForAttachment
        // Currently we are excluding sample parents because they can be assigned to multiple projects.
        .filter((item: SavedItem) => item.type !== ENTITY_TYPE.SAMPLE_PARENT && item.shared)
        .map((item: SavedItem) => {
          const newOption: ISelectAttachmentOption = {
            id: item.id,
            value: item.type + ":" + String(item.id), // in order to avoid collisions
            type: item.type,
            label: item.name,
            created: item.created,
            owner: item.owner,
          };

          switch (item.type) {
            case ENTITY_TYPE.VIEW:
              newOption.description = getViewTitle(item as View);
              break;
            case ENTITY_TYPE.REGION_FLAG:
              newOption.description = `${item.typeDisplayName}`;
              break;
            default:
              // eslint-disable-next-line no-case-declarations
              const group: Group = item as Group;
              newOption.description = `${group.itemCount || 0} ${pluralizeWord(item.typeDisplayName, group.itemCount)}`;
          }

          return newOption;
        });
    }
    if (projectFilesForAttachment) {
      optionList = optionList.concat(
        projectFilesForAttachment.map((item: ProjectFile) => {
          const newOption: ISelectAttachmentOption = {
            id: item.id,
            value: FILE_TYPE + ":" + String(item.id),
            type: FILE_TYPE,
            label: item.fileName,
            description: item.typeDisplayName,
            created: item.created,
            owner: item.creator?.username,
            organisms: item.organisms?.map((o) => o.name),
            parents: item.parents?.map((p) => p.name),
          };
          return newOption;
        }),
      );
    }

    const byCreated = descend(prop<number>("created"));
    optionList = sort(byCreated, optionList);
    setItemOptions(optionList);
  }, [savedItemsForAttachment, projectFilesForAttachment]);

  const selectOptionRenderer = useCallback(
    (option: ISelectAttachmentOption, labelMeta: FormatOptionLabelMeta<ISelectAttachmentOption>) => {
      return <CommentAttachmentOption option={option} isDropdownOption={labelMeta.context === "menu"} />;
    },
    [],
  );

  const customFilter = useCallback((option: { label: string; data: ISelectAttachmentOption }, searchText: string) => {
    return !!(
      option.label?.toLowerCase().includes(searchText.toLowerCase()) ||
      option.data.organisms?.find((o) => o.toLowerCase().includes(searchText.toLowerCase())) ||
      option.data.parents?.find((o) => o.toLowerCase().includes(searchText.toLowerCase())) ||
      option.data.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      option.data.owner?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, []);

  return (
    <div style={{ width: 700, height: 300, padding: 10 }} className="addAttachmentPanel">
      <FormInputGroup>
        <FormLabel>Select Attachments</FormLabel>
        <FormInputWidget>
          <WindowedSelect
            // menuPortalTarget={document.body}
            isMulti
            className="react-select"
            classNamePrefix={"react-select"}
            name="selectAttachment"
            placeholder={ATTACHMENT_PLACEHOLDER_TEXT}
            autoFocus={true}
            //openMenuOnFocus={true}
            //openMenuOnClick={true}
            isClearable={false}
            isSearchable={true}
            formatOptionLabel={selectOptionRenderer}
            components={{ IndicatorsContainer: () => null }}
            value={props.selectedAttachments}
            options={itemOptions}
            onChange={props.onSelect}
            noOptionsMessage={() => (isLoading ? "Loading..." : "No potential attachments found")}
            closeMenuOnSelect={false}
            menuPlacement={"bottom"}
            maxMenuHeight={200}
            menuIsOpen={true}
            blurInputOnSelect={false}
            filterOption={customFilter}
          />
        </FormInputWidget>
      </FormInputGroup>
    </div>
  );
};

interface CommentAttachmentOptionProps {
  option: ISelectAttachmentOption;
  isDropdownOption: boolean;
}
export const CommentAttachmentOption: FC<CommentAttachmentOptionProps> = (props) => {
  const { option, isDropdownOption } = props;
  const titleClass: string = isDropdownOption ? "item-count" : null;
  const titleStyle: CSSProperties = isDropdownOption ? null : { fontWeight: "bold" };
  const labelStyle: CSSProperties = isDropdownOption
    ? { fontWeight: "bold", color: "rgb(58, 143, 200)" }
    : { fontWeight: "bold" };

  const legend: ReactElement = (
    <span className={titleClass} style={titleStyle}>
      ({option.description})
    </span>
  );
  const savedHint: ReactElement = isDropdownOption ? (
    <span className="saved-hint">
      saved <TimeAgo dateTimeISO={option.created} /> {option.owner ? "by " + option.owner : ""}
    </span>
  ) : null;

  const tags: ReactElement =
    isDropdownOption && option.organisms ? (
      <>
        {option.organisms.map((o, i) => (
          <Tag label={o} intentColor={TAG_COLOR} key={`attachment-select-panel-option-org-${i}`} />
        ))}
        {option.parents.map((o, i) => (
          <Tag label={o} intentColor={TAG_COLOR} key={`attachment-select-panel-option-parent-${i}`} />
        ))}
      </>
    ) : null;

  return (
    <span>
      <span style={labelStyle}>{option.label}</span> {legend} {savedHint} {tags}
    </span>
  );
};
